import { type ColombianValidationSchema, type DefaultValidationSchema, type PeruValidationSchema } from './types';
import {
  asEmail,
  asMaxLength,
  asName,
  asPersonalId,
  asFrenchPersonalId,
  asPortuguesePersonalId,
  asCodiceFiscale,
  asPhoneNumber,
  asFrenchPhoneNumber,
  asPortuguesePhoneNumber,
  asSalary,
  asColombianId,
  asFrenchIban,
  asPeruId,
  asWorkingTimePercentage
} from './validator';
import { asIban } from 'src/shared/utils/validators';

const defaultStateValidatorSchema: DefaultValidationSchema = {
  firstName: {
    required: true,
    validator: {
      validate: (value: string) => asName(value, 'nombre')
    }
  },
  lastName: {
    required: true,
    validator: {
      validate: (value: string) => asName(value, 'apellido')
    }
  },
  personalId: {
    required: true,
    validator: {
      validate: (value: string) => asPersonalId(value)
    }
  },
  email: {
    required: false,
    validator: {
      validate: (value: string) => asEmail(value)
    }
  },
  iban: {
    required: true,
    validator: {
      validate: (value: string) => asIban(value)
    }
  },
  phone: {
    required: false,
    validator: {
      validate: (value: string) => asPhoneNumber(value)
    }
  },
  salary: {
    required: true,
    validator: {
      validate: (value: string) => asSalary(value)
    }
  },
  workingTimePercentage: {
    required: false,
    validator: {
      validate: (value: number | null | undefined | string) => asWorkingTimePercentage(value)
    }
  }
};

const getStateValidatorSchemaByLocale = (
  locale: string,
  values = {}
): DefaultValidationSchema | ColombianValidationSchema | PeruValidationSchema => {
  switch (locale) {
    case 'fr-FR':
      return {
        ...defaultStateValidatorSchema,
        personalId: {
          required: true,
          validator: {
            validate: (value: string) => asFrenchPersonalId(value)
          }
        },
        iban: {
          ...defaultStateValidatorSchema.iban,
          validator: {
            validate: (value: string) => asFrenchIban(value)
          }
        },
        phone: {
          ...defaultStateValidatorSchema.phone,
          validator: {
            validate: (value: string) => asFrenchPhoneNumber(value)
          }
        }
      };
    case 'it-IT':
      return {
        ...defaultStateValidatorSchema,
        personalId: {
          required: true,
          validator: {
            validate: (value: string) => asCodiceFiscale(value)
          }
        }
      };
    case 'pt-PT':
      return {
        ...defaultStateValidatorSchema,
        personalId: {
          required: true,
          validator: {
            validate: (value: string) => asPortuguesePersonalId(value)
          }
        },
        phone: {
          ...defaultStateValidatorSchema.phone,
          validator: {
            validate: (value: string) => asPortuguesePhoneNumber(value)
          }
        }
      };
    case 'es-CO':
      return {
        ...defaultStateValidatorSchema,
        bankAccountNumber: {
          required: true
        },
        bic_swift: {
          required: true,
          validator: {
            validate: (value: string) => asMaxLength(value, 40)
          }
        },
        address: {
          required: true,
          validator: {
            validate: (value: string) => asMaxLength(value, 100)
          }
        },
        personalId: {
          required: true,
          validator: {
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            validate: (value: string, values: any) => asColombianId(value, values.documentType)
          }
        },
        phone: {
          required: false
        },
        salary: {
          required: true
        }
      };
    case 'es-PE':
      return {
        ...defaultStateValidatorSchema,
        bankAccountNumber: {
          required: true
        },
        bic_swift: {
          required: true,
          validator: {
            validate: (value: string) => asMaxLength(value, 40)
          }
        },
        personalId: {
          required: true,
          validator: {
            validate: (value: string) => asPeruId(value)
          }
        },
        phone: {
          required: false
        },
        salary: {
          required: true
        }
      };
    default:
      return defaultStateValidatorSchema;
  }
};

export default getStateValidatorSchemaByLocale;
